<script setup>
/**
 * CrmTimeline — trilho vertical read-only de eventos do deal (F2).
 *
 * Presentational puro: recebe `events` (deal_events do contrato, ASC por ocorreu_em)
 * e renderiza a mesma linguagem visual da timeline antiga do CrmDealPanel — trilho
 * vertical n-weak, um dot por evento, linha de rótulo + datetime + autor.
 *
 * Brand-only / monocromático: ganho = dot ink (n-slate-12); todo o resto — inclusive
 * perda — = dot slate atenuado (n-slate-8). Sem matiz nos dados (won=ink, lost=cinza).
 *
 * Eventos cujo tipo não tem rótulo (desconhecidos / ainda não emitidos na F2) são
 * filtrados — a timeline degrada com elegância em vez de quebrar.
 */
import { computed } from 'vue';
import {
  dealEventLabel,
  eventIconFor,
  isEmphasisEvent,
} from './composables/useDealEventVisuals.js';
import {
  formatDataDdMm,
  formatHoraCurta,
} from './composables/useMetasFormat.js';

const props = defineProps({
  events: { type: Array, default: () => [] },
});

// Só renderiza eventos com rótulo conhecido; carrega o label resolvido junto
// para não recomputar no template.
const renderableEvents = computed(() =>
  (props.events ?? [])
    .map(event => ({ event, label: dealEventLabel(event) }))
    .filter(({ label }) => label != null)
);

function fmtDateTime(iso) {
  const data = formatDataDdMm(iso);
  if (!data) return '—';
  return `${data} ${formatHoraCurta(iso)}`;
}

function authorName(event) {
  if (event?.ator_sistema) return 'Sistema';
  return event?.ator?.name ?? 'Sistema';
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div v-if="renderableEvents.length" class="relative pl-5">
    <!-- Trilho vertical -->
    <div class="absolute left-[5px] top-1 bottom-3 w-px bg-n-weak" />

    <div
      v-for="({ event, label }, idx) in renderableEvents"
      :key="event.id ?? idx"
      class="relative pb-3 last:pb-0"
    >
      <!-- Dot: ganho = ink (ênfase); demais = slate atenuado. Sem matiz. -->
      <span
        class="absolute -left-5 top-0.5 size-2.5 rounded-full border-2 border-n-background"
        :class="[isEmphasisEvent(event) ? 'bg-n-slate-12' : 'bg-n-slate-8']"
      />

      <!-- Rótulo + datetime -->
      <div class="flex items-baseline justify-between gap-2">
        <span class="flex items-center gap-1.5 min-w-0">
          <span
            class="size-3 flex-shrink-0"
            :class="[
              eventIconFor(event.tipo),
              isEmphasisEvent(event) ? 'text-n-slate-12' : 'text-n-slate-10',
            ]"
          />
          <span class="text-mj-12 font-500 text-n-slate-12 truncate">{{
            label
          }}</span>
        </span>
        <span class="text-mj-11 text-n-slate-10 tabular-nums flex-shrink-0">
          {{ fmtDateTime(event.ocorreu_em) }}
        </span>
      </div>

      <!-- Autor -->
      <p class="text-mj-11 text-n-slate-10 mt-0.5 pl-[18px]">
        por {{ authorName(event) }}
      </p>
    </div>
  </div>

  <p v-else class="text-mj-13 text-n-slate-10 italic">
    Sem eventos registrados.
  </p>
</template>
